import {
  apiGetListCourseFolders,
  apiDeleteFileFromFolder,
  apiCreateEcosShareLink,
  apiCreateEcosShareLink_Attest,
  apiCreateDiplomaImageShareLink,
  apiDeleteCourseFolder,
  apiDeleteFileFromCourseFolder,
  apiDeleteFileFromDiplomaFolder,
  apiCreateFolderOneDrive,
  setAuthHeaderForMsGraph,
  apiGetListFilesByAccountName,
  apiUpdateItem,
  apiUploadDocXFile,
  apiUploadSimpleFile,
  apiUploadPDFFile,
  apiConvertToPdf,
  apiConvertWordToPdf,
  apiGetListFilesCurrentAccount,
  apiDownloadItem,
  apiSearchRootItemByName,
  apiSearchItem_In_RootItemByName,
  apiGetPhotoCurrentAccount,
  apiGetListUsers,
  apiCreateEventCalendar,
  apiGetListGroupMembers,
  apiGetCalendarCurrentAccount
} from "./apiMicrosoft";

import {
  apiGoogleListFolderContents,
  apiGoogleCreateShareLink,
  apiGoogleCreateEcosShareLink,
  apiGoogleDeleteFile,
  apiGoogleCreateFolder,
  apiGoogleSearchFileByName,
  apiGoogleResolvePath
} from "./apiGoogleDrive";

import { stopProactiveGoogleTokenRefresh } from "./googleDriveTokenManager";

const GOOGLE_EMAIL_KEY = "google_email";

export function getProvider() {
  const googleEmail = localStorage.getItem(GOOGLE_EMAIL_KEY);
  return googleEmail ? "google" : "microsoft";
}

// List folder contents - returns { data: { value: [...] } } for both providers
export const cloudGetListFolderContents = async (pathOrFolderId) => {
  const provider = getProvider();
  if (provider === "google") {
    const folderId = pathOrFolderId && pathOrFolderId.includes('/')
      ? await apiGoogleResolvePath(pathOrFolderId)
      : pathOrFolderId;
    if (!folderId) return { data: { value: [] } };
    return apiGoogleListFolderContents(folderId);
  }
  return apiGetListCourseFolders(pathOrFolderId);
};

// Delete a file
export const cloudDeleteFileFromFolder = async (pathOrFolderId, fileName, fileId) => {
  const provider = getProvider();
  if (provider === "google") {
    // Use file ID if provided (stored in MongoDB as idImage)
    if (fileId) return apiGoogleDeleteFile(fileId);
    // Fallback: search by name in the folder
    const folderId = pathOrFolderId && pathOrFolderId.includes('/')
      ? await apiGoogleResolvePath(pathOrFolderId)
      : pathOrFolderId;
    if (folderId) {
      const contents = await apiGoogleListFolderContents(folderId);
      const found = contents?.data?.value?.find(f => f.name === fileName);
      if (found) return apiGoogleDeleteFile(found.id);
    }
    // Last resort: try fileName as a raw ID
    return apiGoogleDeleteFile(fileName);
  }
  return apiDeleteFileFromFolder(pathOrFolderId, fileName);
};

// Create a share link - returns { data: { link: { webUrl: "..." } } }
export const cloudCreateEcosShareLink = async (pathOrFileId) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleCreateEcosShareLink(pathOrFileId);
  }
  return apiCreateEcosShareLink(pathOrFileId);
};

// Create diploma share link
export const cloudCreateDiplomaImageShareLink = async (fileIdOrName) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleCreateShareLink(fileIdOrName);
  }
  return apiCreateDiplomaImageShareLink(fileIdOrName);
};

// Delete course folder
export const cloudDeleteCourseFolder = async (folderNameOrId) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleDeleteFile(folderNameOrId);
  }
  return apiDeleteCourseFolder(folderNameOrId);
};

// Delete file from course folder
export const cloudDeleteFileFromCourseFolder = async (folderNameOrId, fileNameOrId) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleDeleteFile(fileNameOrId);
  }
  return apiDeleteFileFromCourseFolder(folderNameOrId, fileNameOrId);
};

// Delete from diploma folder
export const cloudDeleteFileFromDiplomaFolder = async (itemNameOrId, fileId) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleDeleteFile(fileId || itemNameOrId);
  }
  return apiDeleteFileFromDiplomaFolder(itemNameOrId);
};

// Create folder
export const cloudCreateFolder = async (data) => {
  const provider = getProvider();
  if (provider === "google") {
    return apiGoogleCreateFolder(data);
  }
  return apiCreateFolderOneDrive(data);
};

// Get the upload endpoint URL and metadata key name
export const getCloudUploadConfig = () => {
  const provider = getProvider();
  if (provider === "google") {
    return {
      endpoint: '/xhrUploadGoogleDriveFile',
      diplomaEndpoint: '/xhrUploadGoogleDriveDiploma',
      certificateEndpoint: '/xhrUploadGoogleDriveCertificate',
      tokenKey: 'google_drive_token',
      parentIdKey: 'parentFolderId'
    };
  }
  return {
    endpoint: '/xhrUploadFile',
    diplomaEndpoint: '/xhrUploadDiploma',
    certificateEndpoint: '/xhrUploadFile',
    tokenKey: 'fhg48_UfGhra8_ph',
    parentIdKey: 'path'
  };
};

function logoutGoogle() {
  localStorage.removeItem("comis-app-token");
  localStorage.removeItem("google_email");
  localStorage.removeItem("google_drive_token");
  localStorage.removeItem("google_drive_token_expires_at");
  stopProactiveGoogleTokenRefresh();
  window.location.hash = "#/login";
  window.location.reload();
}

function checkGoogleTokenExpiry() {
  const token = localStorage.getItem("google_drive_token");
  const expiresAt = localStorage.getItem("google_drive_token_expires_at");
  if (!token || !expiresAt || Date.now() >= parseInt(expiresAt, 10) - 60000) {
    logoutGoogle();
    return false;
  }
  return true;
}

let expiryCheckTimer = null;

export function startGoogleTokenExpiryCheck() {
  stopGoogleTokenExpiryCheck();
  expiryCheckTimer = setInterval(() => {
    const googleEmail = localStorage.getItem("google_email");
    if (googleEmail) {
      checkGoogleTokenExpiry();
    }
  }, 30 * 1000);
}

export function stopGoogleTokenExpiryCheck() {
  if (expiryCheckTimer) {
    clearInterval(expiryCheckTimer);
    expiryCheckTimer = null;
  }
}

// Get a fresh token for the current provider
export const cloudGetFreshToken = async () => {
  const provider = getProvider();
  if (provider === "google") {
    const token = localStorage.getItem("google_drive_token");
    const expiresAt = localStorage.getItem("google_drive_token_expires_at");
    if (token && expiresAt && Date.now() < parseInt(expiresAt, 10) - 60000) {
      return token;
    }
    logoutGoogle();
    return null;
  }
  return localStorage.getItem("fhg48_UfGhra8_ph");
};

// Cloud-aware wrapper for apiGetListFilesByAccountName
export const cloudGetListFilesByAccountName = async (userName, path) => {
  const provider = getProvider();
  if (provider === "google") {
    const folderId = path && path.includes('/') ? await apiGoogleResolvePath(path) : path;
    if (!folderId) return { data: { value: [] } };
    const result = await apiGoogleListFolderContents(folderId);
    // Normalize Google Drive file properties to match Microsoft Graph schema
    if (result.data && result.data.value) {
      result.data.value = result.data.value.map(f => ({
        ...f,
        id: f.id,
        name: f.name,
        webUrl: f.webViewLink || f.webContentLink,
        image: f.image ? f.image : (f.mimeType && f.mimeType.startsWith('image/') ? { width: 1200, height: 900 } : { width: 170, height: 125 })
      }));
    }
    return result;
  }
  return apiGetListFilesByAccountName(userName, path);
};

// Re-export MS-specific functions for backward compatibility
export {
  apiCreateEcosShareLink,
  apiCreateEcosShareLink_Attest,
  apiCreateDiplomaImageShareLink,
  apiDeleteFileFromFolder,
  apiDeleteFileFromCourseFolder,
  apiDeleteFileFromDiplomaFolder,
  apiCreateFolderOneDrive,
  apiGetListCourseFolders,
  apiDeleteCourseFolder,
  setAuthHeaderForMsGraph,
  cloudGetListFilesByAccountName as apiGetListFilesByAccountName,
  apiUpdateItem,
  apiUploadDocXFile,
  apiUploadSimpleFile,
  apiUploadPDFFile,
  apiConvertToPdf,
  apiConvertWordToPdf,
  apiGetListFilesCurrentAccount,
  apiDownloadItem,
  apiSearchRootItemByName,
  apiSearchItem_In_RootItemByName,
  apiGetPhotoCurrentAccount,
  apiGetListUsers,
  apiCreateEventCalendar,
  apiGetListGroupMembers,
  apiGetCalendarCurrentAccount
};